import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import ErrorBoundary from "@/components/ErrorBoundary";

// Public / auth pages are eager — they are needed before any user logs in
// and we don't want a flash-of-spinner on the unauthenticated landing.
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import CustomerOrder from "@/pages/CustomerOrder";
import TableQR from "@/pages/TableQR";
import PaymentReturn from "@/pages/PaymentReturn";
import Queue from "@/pages/Queue";
// iter_ONLINE_CUSTOMER_BOOKINGS (2026-02-28) — public booking pages
// (eager, no auth).
import PublicBooking from "@/pages/PublicBooking";
import PublicBookingManage from "@/pages/PublicBookingManage";
// iter_CONFIGURABLE_PUBLIC_VENUE_LANDING_PAGE (2026-02-28) — public
// venue landing + contact pages (eager, no auth).
import PublicVenueLanding from "@/pages/PublicVenueLanding";
import PublicVenueContact from "@/pages/PublicVenueContact";
import PendingApproval from "@/pages/PendingApproval";
import Layout from "@/components/Layout";

// Private/admin pages are lazy — each route gets its own chunk so the
// /login bundle no longer ships Overheads/POS/Settings/Recipes/etc.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const POS = lazy(() => import("@/pages/POS"));
const HeldOrders = lazy(() => import("@/pages/HeldOrders"));
const OrdersHistory = lazy(() => import("@/pages/OrdersHistory"));
const KDS = lazy(() => import("@/pages/KDS"));
const Delivery = lazy(() => import("@/pages/Delivery"));
const MenuBuilder = lazy(() => import("@/pages/MenuBuilder"));
const Modifiers = lazy(() => import("@/pages/Modifiers"));
const Recipes = lazy(() => import("@/pages/Recipes"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Packaging = lazy(() => import("@/pages/Packaging"));
const Announcements = lazy(() => import("@/pages/Announcements"));
const InvoiceUpload = lazy(() => import("@/pages/InvoiceUpload"));
const Stocktake = lazy(() => import("@/pages/Stocktake"));
const StockCounters = lazy(() => import("@/pages/StockCounters"));
const Customers = lazy(() => import("@/pages/Customers"));
const Vouchers = lazy(() => import("@/pages/Vouchers"));
const Loyalty = lazy(() => import("@/pages/Loyalty"));
const Overheads = lazy(() => import("@/pages/Overheads"));
const ProfitInsights = lazy(() => import("@/pages/ProfitInsights"));
const CashflowForecast = lazy(() => import("@/pages/CashflowForecast"));
const Reports = lazy(() => import("@/pages/Reports"));
// iter_216 / Phase C (2026-02-28) — Platform Cleanup. The legacy
// Settings page (Settings.jsx) is no longer the user-facing settings
// experience. /settings redirects to /settings/v2 below; the file is
// still imported as the single source-of-truth for the form widget
// components that SettingsV2 composes.
const SettingsV2 = lazy(() => import("@/pages/SettingsV2"));
// iter_P2 Phase 2B (2026-02-23) — Printers & Cash Drawers Settings page.
const PrintersSettings = lazy(() => import("@/pages/PrintersSettings"));
const Till = lazy(() => import("@/pages/Till"));
const Bookings = lazy(() => import("@/pages/Bookings"));
// iter_CONSIGNMENT_SALES (2026-02-28) — simple consignment-stock feature.
const Consignors = lazy(() => import("@/pages/Consignors"));
const ConsignorStatement = lazy(() => import("@/pages/ConsignorStatement"));
// iter_DRIVER_PAYOUTS (2026-02-28) — driver payout ledger + statement.
const DriverPayouts = lazy(() => import("@/pages/DriverPayouts"));
const DriverStatement = lazy(() => import("@/pages/DriverStatement"));
const BillingReturn = lazy(() => import("@/pages/BillingReturn"));
const CustomerDisplay = lazy(() => import("@/pages/CustomerDisplay"));
// iter_MOTO_ISOLATED (2026-02-28) — dedicated /moto route. Default OFF
// via MOTO_KILL_SWITCH_ACTIVE=true + MOTO_ENABLED=false; the page
// itself renders the gate state and refuses to start a payment when
// any condition is false. No card-entry fields anywhere.
const MotoPage = lazy(() => import("@/pages/MotoPage"));
const Kiosk = lazy(() => import("@/pages/Kiosk"));
const DeviceSetup = lazy(() => import("@/pages/DeviceSetup"));
// iter_PLATFORM_OWNER_TENANT_MANAGEMENT (2026-02-28) — Platform Owner
// tenant admin surface. Access is server-side gated by the
// PLATFORM_OWNER_EMAILS env var; the route sits inside <Private> so
// unauthenticated users are bounced to /login.
const PlatformAdmin = lazy(() => import("@/pages/PlatformAdmin"));
// iter_200 (2026-02-22) — Stripe Terminal Ops Dashboard removed.
// The new rebuilt Stripe Terminal module is single-card-in-Settings
// only; no separate dashboard route.

function LoadingShell({ testId = "route-loading" }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[var(--sand-50)]"
      data-testid={testId}
    >
      <div className="text-sm text-[var(--ink-500)]">Loading…</div>
    </div>
  );
}

function Private({ children }) {
  const { user, loading } = useAuth();
  if (localStorage.getItem("mm_kiosk_token")) return <Navigate to="/kiosk" replace />;
  if (loading) {
    return <LoadingShell testId="app-loading" />;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// 2026-02-27 — Staff access lockdown. Staff role is limited to the 6
// operational pages below. Any other admin URL is redirected to /pos.
// Owner / manager / kitchen / delivery_driver are NOT affected — they
// pass through unchanged so their existing behaviour stays intact.
const STAFF_ALLOWED_ROUTES = [
  "/pos", "/orders", "/held", "/till", "/kds", "/delivery", "/bookings", "/device",
];

// iter_89 — Restricted roles (kitchen / delivery_driver) are even more
// narrowly scoped than staff and must never see Bookings or other admin
// surfaces by URL. Pre-existing behaviour: kitchen lives on /kds, the
// delivery_driver lives on /delivery. We surface this explicitly here
// so the backend 403 isn't the only line of defence.
const KITCHEN_ALLOWED_ROUTES = ["/kds"];
const DELIVERY_DRIVER_ALLOWED_ROUTES = ["/delivery"];

function StaffGuard({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const path = loc.pathname;
  const role = user?.role;
  const matches = (routes) =>
    routes.some((r) => path === r || path.startsWith(r + "/"));
  if (role === "staff" && !matches(STAFF_ALLOWED_ROUTES)) {
    return <Navigate to="/pos" replace />;
  }
  if (role === "kitchen" && !matches(KITCHEN_ALLOWED_ROUTES)) {
    return <Navigate to="/kds" replace />;
  }
  if (role === "delivery_driver" && !matches(DELIVERY_DRIVER_ALLOWED_ROUTES)) {
    return <Navigate to="/delivery" replace />;
  }
  return children;
}

// Staff land directly on POS post-login — never flash the Dashboard.
function RootRoute() {
  const { user } = useAuth();
  if (user?.role === "staff") {
    return <Navigate to="/pos" replace />;
  }
  return <Dashboard />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (localStorage.getItem("mm_kiosk_token")) return <Navigate to="/kiosk" replace />;
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-right" richColors theme="light" />
            <Suspense fallback={<LoadingShell />}>
              <Routes>
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />

              {/* Customer ordering (kiosk/qr/online) public */}
              <Route path="/order" element={<CustomerOrder />} />
              <Route path="/t/:token" element={<TableQR />} />
              <Route path="/pay/success" element={<PaymentReturn outcome="success" />} />
              <Route path="/pay/cancel" element={<PaymentReturn outcome="cancel" />} />
              <Route path="/auth/google/callback" element={<AuthCallback />} />
              <Route path="/queue" element={<Queue />} />
              {/* iter_ONLINE_CUSTOMER_BOOKINGS — public booking flow */}
              <Route path="/book" element={<PublicBooking />} />
              <Route path="/book/:venueId" element={<PublicBooking />} />
              <Route path="/book/manage/:token" element={<PublicBookingManage />} />
              {/* iter_CONFIGURABLE_PUBLIC_VENUE_LANDING_PAGE — public venue pages */}
              <Route path="/venue/:venueId" element={<PublicVenueLanding />} />
              <Route path="/venue/:venueId/contact" element={<PublicVenueContact />} />
              <Route path="/customer-display" element={<CustomerDisplay />} />
              <Route path="/kiosk" element={<Kiosk />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/pending-approval" element={<PendingApproval />} />
              <Route path="/billing/return" element={<Private><BillingReturn /></Private>} />

              {/* iter_PLATFORM_OWNER_TENANT_MANAGEMENT — Platform Owner
                  surface. NOT inside StaffGuard/Layout because the
                  Platform Owner may not have a tenant that has been
                  approved yet (or may want to review before any tenant
                  is set up). Access is enforced server-side. */}
              <Route path="/platform-admin" element={<Private><PlatformAdmin /></Private>} />

              <Route element={<Private><StaffGuard><Layout /></StaffGuard></Private>}>
                <Route path="/" element={<RootRoute />} />
                <Route path="/pos" element={<POS />} />
                <Route path="/device" element={<DeviceSetup />} />
                <Route path="/moto" element={<MotoPage />} />
                <Route path="/held" element={<HeldOrders />} />
                <Route path="/orders" element={<OrdersHistory />} />
                <Route path="/kds" element={<KDS />} />
                <Route path="/delivery" element={<Delivery />} />
                <Route path="/menu" element={<MenuBuilder />} />
                <Route path="/modifiers" element={<Modifiers />} />
                <Route path="/recipes" element={<Recipes />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/packaging" element={<Packaging />} />
                <Route path="/announcements" element={<Announcements />} />
                <Route path="/invoice" element={<InvoiceUpload />} />
                <Route path="/stocktake" element={<Stocktake />} />
                <Route path="/stock-counters" element={<StockCounters />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/vouchers" element={<Vouchers />} />
                <Route path="/loyalty" element={<Loyalty />} />
                <Route path="/overheads" element={<Overheads />} />
                <Route path="/profit-insights" element={<ProfitInsights />} />
                <Route path="/cashflow" element={<CashflowForecast />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/settings" element={<Navigate to="/settings/v2" replace />} />
                <Route path="/settings/v2" element={<SettingsV2 />} />
                <Route path="/settings/printers" element={<PrintersSettings />} />
                <Route path="/till" element={<Till />} />
                <Route path="/bookings" element={<Bookings />} />
                <Route path="/consignors" element={<Consignors />} />
                <Route path="/consignors/:id/statement" element={<ConsignorStatement />} />
                <Route path="/drivers" element={<DriverPayouts />} />
                <Route path="/drivers/:id/statement" element={<DriverStatement />} />
                {/* iter_200 — Legacy /stripe-terminal-ops dashboard removed
                    in the WisePOS E rebuild. Settings → Stripe Terminal is
                    now the single source of truth (manager+). */}
              </Route>
              <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
        </AuthProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
